/**
 * Pure resolver for a row's logical layout shape (`stackable`,
 * `spannable`, `minWidthTimescale`).
 *
 * Reads `RowTypeSpec.layoutShape` for a row's wire type. Falls back
 * to a neutral shape (no stacking, no spanning) when the type has
 * no entry — matches DOM's `DEFAULT_LAYOUT` behavior at
 * `tento/tento-lona-js/src/rows/row-descriptor.ts:272-280`.
 *
 * Does not return pixel heights; those live on the DOM-side
 * `RowLayoutHeights` bundle and are looked up separately by DOM
 * renderers.
 */

import type { Option } from "../core/option.ts";
import type { JsonObject } from "../core/json.ts";
import type { RowLayoutShape } from "./layout-types.ts";
import { SheetRegistries } from "../sheet/sheet-registries.ts";

/**
 * The layout shape of a row type with no `layoutShape` entry (neither stackable nor spannable),
 * which the layout-shape resolver falls back to.
 */
const NEUTRAL_SHAPE: RowLayoutShape = Object.freeze({
  stackable: false,
  spannable: false,
  minWidthTimescale: null,
});

/**
 * Resolve the logical layout shape for a row. The CLI / future
 * non-DOM renderers consume this to feed `RowRenderMode.compute`
 * without going through DOM's `Descriptor.layoutSpec`.
 */
export function resolveLayoutShape(
  rowType: string,
  attributes: Option<JsonObject>,
  sheetTimescale: string,
): RowLayoutShape {
  // The built-in specs: the resolver serves renderers with no sheet at hand
  // (the lona-side row descriptors, the CLI's layout pass).
  const spec = SheetRegistries.BUILTINS.rowTypes.get(rowType);
  if (!spec?.layoutShape) return NEUTRAL_SHAPE;
  return spec.layoutShape(attributes, sheetTimescale);
}

export { NEUTRAL_SHAPE as DEFAULT_LAYOUT_SHAPE };
